import React, { useState, useEffect } from 'react';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from "firebase/firestore";
import { db } from '../../firebase/firestore';
import { 
  Gift, 
  X, 
  Check, 
  Loader, 
  BookOpen,
  Timer,
  Clock,
  ShieldOff,
  UserPlus,
  Search
} from 'lucide-react';

const GiftManager = ({ isOpen, onClose, user, onGiftIssued }) => {
  const [trainings, setTrainings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingGifts, setLoadingGifts] = useState(false);
  const [selectedGiftItems, setSelectedGiftItems] = useState([]);
  const [userGifts, setUserGifts] = useState([]);
  const [activeTab, setActiveTab] = useState('available'); // available, issued
  
  // Настройки подарка
  const [giftDuration, setGiftDuration] = useState(7);
  const [giftTimerEnabled, setGiftTimerEnabled] = useState(true);
  const [giftMessage, setGiftMessage] = useState('');
  
  // Поиск
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);

  // Загрузка материалов для подарков
  const fetchGiftItems = async () => {
    setLoading(true);
    try {
      // Загрузка тренингов
      const trainingsSnap = await getDocs(collection(db, "trainings"));
      const trainingsData = trainingsSnap.docs.map(doc => ({
        id: doc.id,
        type: 'training',
        ...doc.data()
      }));

      // Загрузка курсов
      const coursesSnap = await getDocs(collection(db, "courses"));
      const coursesData = coursesSnap.docs.map(doc => ({
        id: doc.id,
        type: 'course',
        ...doc.data()
      }));

      setTrainings(trainingsData);
      setCourses(coursesData);
      setFilteredTrainings(trainingsData);
      setFilteredCourses(coursesData);
      
    } catch (error) {
      console.error("Ошибка загрузки материалов:", error);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка подарков пользователя из подколлекции
  const fetchUserGifts = async () => {
    if (!user) return;
    
    setLoadingGifts(true);
    try {
      const giftsRef = collection(db, "telegramUsers", user.id, "gifts");
      const q = query(giftsRef, orderBy("issuedAt", "desc"));
      
      const querySnapshot = await getDocs(q);
      const gifts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        expiresAt: doc.data().expiresAt?.toDate?.() || new Date(doc.data().expiresAt),
        issuedAt: doc.data().issuedAt?.toDate?.() || new Date(doc.data().issuedAt)
      }));
      
      setUserGifts(gifts);
    } catch (error) {
      console.error("Ошибка загрузки подарков:", error);
    } finally {
      setLoadingGifts(false);
    }
  };

  // Фильтрация по поиску
  useEffect(() => {
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      setFilteredTrainings(
        trainings.filter(t => t.title?.toLowerCase().includes(term))
      );
      setFilteredCourses(
        courses.filter(c => c.title?.toLowerCase().includes(term))
      );
    } else {
      setFilteredTrainings(trainings);
      setFilteredCourses(courses);
    }
  }, [searchTerm, trainings, courses]);

  // Загрузка данных при открытии
  useEffect(() => {
    if (isOpen && user) {
      fetchGiftItems();
      fetchUserGifts();
    }
  }, [isOpen, user]);

  // Сброс состояния при закрытии
  useEffect(() => {
    if (!isOpen) {
      setSelectedGiftItems([]);
      setGiftDuration(7);
      setGiftTimerEnabled(true);
      setGiftMessage('');
      setSearchTerm('');
      setActiveTab('available');
    }
  }, [isOpen]);

  // Тоггл выбора подарка
  const toggleGiftItem = (item) => {
    setSelectedGiftItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  // Выдача подарка
  const handleGrantGift = async () => {
    if (!user || selectedGiftItems.length === 0) return;

    setLoading(true);
    try {
      const now = new Date();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + giftDuration);

      const giftsRef = collection(db, "telegramUsers", user.id, "gifts");

      for (const item of selectedGiftItems) {
        const giftData = {
          itemId: item.id,
          itemType: item.type,
          itemTitle: item.title,
          itemImage: item.image || null,
          issuedAt: Timestamp.fromDate(now),
          expiresAt: Timestamp.fromDate(expiresAt),
          duration: giftDuration,
          hasTimer: giftTimerEnabled,
          message: giftMessage || null,
          status: 'active',
          activatedAt: null,
          issuedBy: 'admin'
        };

        await addDoc(giftsRef, giftData);
      }

      // Обновляем список подарков
      await fetchUserGifts();
      
      // Очищаем выбранные
      setSelectedGiftItems([]);
      setGiftMessage('');
      
      // Уведомляем родительский компонент
      if (onGiftIssued) {
        onGiftIssued(selectedGiftItems.length);
      }
      
    } catch (error) {
      console.error("Ошибка выдачи подарка:", error);
    } finally {
      setLoading(false);
    }
  };

  // Отзыв подарка
  const handleRevokeGift = async (giftId) => {
    if (!window.confirm('Отозвать этот подарок? Пользователь потеряет доступ.')) return;

    try {
      const giftRef = doc(db, "telegramUsers", user.id, "gifts", giftId);
      await updateDoc(giftRef, {
        status: 'revoked',
        revokedAt: Timestamp.fromDate(new Date()),
        revokedBy: 'admin'
      });

      await fetchUserGifts();
    } catch (error) {
      console.error("Ошибка отзыва подарка:", error);
    }
  };

  // Удаление подарка
  const handleDeleteGift = async (giftId) => {
    if (!window.confirm('Удалить запись о подарке? Это действие нельзя отменить.')) return;

    try {
      const giftRef = doc(db, "telegramUsers", user.id, "gifts", giftId);
      await deleteDoc(giftRef);
      await fetchUserGifts();
    } catch (error) {
      console.error("Ошибка удаления подарка:", error);
    }
  };

  // Форматирование даты
  const formatDate = (date) => {
    if (!date) return 'Не указано';
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Получение статуса подарка
  const getGiftStatus = (gift) => {
    if (gift.status === 'revoked') {
      return { label: 'Отозван', color: 'text-red-300', bg: 'bg-red-900/30', border: 'border-red-700' };
    }
    if (gift.status === 'used') {
      return { label: 'Использован', color: 'text-blue-300', bg: 'bg-blue-900/30', border: 'border-blue-700' };
    }
    if (new Date() > gift.expiresAt) {
      return { label: 'Истек', color: 'text-yellow-300', bg: 'bg-yellow-900/30', border: 'border-yellow-700' };
    }
    return { label: 'Активен', color: 'text-green-300', bg: 'bg-green-900/30', border: 'border-green-700' };
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl border border-gray-700 flex flex-col">
        {/* Заголовок */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-800 to-gray-900">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-pink-400" />
            <h2 className="text-lg font-semibold text-white">
              Управление подарками
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Вкладки */}
        <div className="px-6 pt-4 border-b border-gray-700">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('available')}
              className={`pb-2 px-1 text-sm font-medium transition-colors relative ${
                activeTab === 'available'
                  ? 'text-pink-400 border-b-2 border-pink-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Доступные материалы
            </button>
            <button
              onClick={() => setActiveTab('issued')}
              className={`pb-2 px-1 text-sm font-medium transition-colors relative ${
                activeTab === 'issued'
                  ? 'text-pink-400 border-b-2 border-pink-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Выданные подарки
              {userGifts.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-800 rounded-full">
                  {userGifts.length}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Информация о пользователе */}
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-900/50 to-purple-900/50 flex items-center justify-center border border-gray-600">
                <UserPlus className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">
                  {user.fullName || 'Пользователь без имени'}
                </p>
                <p className="text-sm text-gray-400">
                  @{user.username || 'без username'} • ID: {user.id.substring(0, 8)}...
                </p>
              </div>
            </div>
          </div>

          {activeTab === 'available' ? (
            /* Вкладка доступных материалов */
            <>
              {/* Настройки подарка */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Длительность доступа (дней)
                  </label>
                  <div className="flex gap-2 mb-2">
                    {[3, 7, 14, 30].map((days) => (
                      <button
                        key={days}
                        onClick={() => setGiftDuration(days)}
                        className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                          giftDuration === days
                            ? 'bg-pink-900/30 border-pink-700 text-pink-300'
                            : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        {days}
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    value={giftDuration}
                    onChange={(e) => setGiftDuration(parseInt(e.target.value) || 7)}
                    min="1"
                    max="365"
                    className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Дополнительные настройки
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={giftTimerEnabled}
                      onChange={(e) => setGiftTimerEnabled(e.target.checked)}
                      className="form-checkbox h-4 w-4 text-pink-500 rounded bg-gray-800 border-gray-700"
                    />
                    <span className="text-sm text-gray-300 flex items-center gap-1">
                      <Timer className="w-4 h-4" />
                      Показывать таймер обратного отсчета
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Таймер будет отображаться у пользователя
                  </p>
                </div>
              </div>

              {/* Сообщение к подарку */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Сообщение к подарку (необязательно)
                </label>
                <textarea
                  value={giftMessage}
                  onChange={(e) => setGiftMessage(e.target.value)}
                  placeholder="Например: Поздравляю с днём рождения! 🎉"
                  className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  rows="2"
                />
              </div>

              {/* Поиск */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Поиск по названию..."
                    className="w-full pl-9 pr-4 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
                  />
                </div>
              </div>

              {/* Выбор материалов */}
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader className="w-8 h-8 text-pink-500 animate-spin" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Тренинги */}
                  {filteredTrainings.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        Тренинги ({filteredTrainings.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filteredTrainings.map((training) => (
                          <button
                            key={training.id}
                            onClick={() => toggleGiftItem(training)}
                            className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                              selectedGiftItems.find(i => i.id === training.id)
                                ? 'bg-pink-900/30 border-pink-700'
                                : 'bg-gray-800/50 border-gray-700 hover:bg-gray-700'
                            }`}
                          >
                            <div className="w-10 h-10 rounded bg-gray-700 overflow-hidden flex-shrink-0">
                              {training.image ? (
                                <img 
                                  src={training.image} 
                                  alt="" 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-500">
                                  📚
                                </div>
                              )}
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className="text-sm font-medium text-white truncate">
                                {training.title}
                              </p>
                              <p className="text-xs text-gray-400">
                                {training.lessons?.length || 0} уроков
                              </p>
                            </div>
                            {selectedGiftItems.find(i => i.id === training.id) && (
                              <Check className="w-5 h-5 text-pink-400" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Курсы */}
                  {filteredCourses.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        Курсы ({filteredCourses.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filteredCourses.map((course) => (
                          <button
                            key={course.id}
                            onClick={() => toggleGiftItem(course)}
                            className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                              selectedGiftItems.find(i => i.id === course.id)
                                ? 'bg-pink-900/30 border-pink-700'
                                : 'bg-gray-800/50 border-gray-700 hover:bg-gray-700'
                            }`}
                          >
                            <div className="w-10 h-10 rounded bg-gray-700 overflow-hidden flex-shrink-0">
                              {course.image ? (
                                <img 
                                  src={course.image} 
                                  alt="" 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-500">
                                  📖
                                </div>
                              )}
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className="text-sm font-medium text-white truncate">
                                {course.title}
                              </p>
                              <p className="text-xs text-gray-400">
                                {course.blocks?.length || 0} блоков
                              </p>
                            </div>
                            {selectedGiftItems.find(i => i.id === course.id) && (
                              <Check className="w-5 h-5 text-pink-400" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {filteredTrainings.length === 0 && filteredCourses.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      <Gift className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Материалы не найдены</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* Вкладка выданных подарков */
            <div>
              {loadingGifts ? (
                <div className="flex justify-center py-8">
                  <Loader className="w-8 h-8 text-pink-500 animate-spin" />
                </div>
              ) : userGifts.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Gift className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>У пользователя пока нет подарков</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {userGifts.map((gift) => {
                    const status = getGiftStatus(gift);
                    const isExpired = new Date() > gift.expiresAt && gift.status === 'active';
                    
                    return (
                      <div
                        key={gift.id}
                        className="bg-gray-800/50 rounded-lg p-4 border border-gray-700"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded bg-gray-700 overflow-hidden flex-shrink-0">
                              {gift.itemImage ? (
                                <img 
                                  src={gift.itemImage} 
                                  alt="" 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-500">
                                  {gift.itemType === 'training' ? '📚' : '📖'}
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {gift.itemTitle}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`text-xs px-2 py-0.5 rounded-full border ${status.bg} ${status.color} ${status.border}`}>
                                  {status.label}
                                </span>
                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDate(gift.issuedAt)}
                                </span>
                                {gift.hasTimer && (
                                  <span className="text-xs text-pink-400 flex items-center gap-1">
                                    <Timer className="w-3 h-3" />
                                    {gift.duration} дн.
                                  </span>
                                )}
                              </div>
                              {gift.message && (
                                <p className="text-sm text-gray-300 mt-2 italic">
                                  "{gift.message}"
                                </p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                Действует до: {formatDate(gift.expiresAt)}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {gift.status === 'active' && !isExpired && (
                              <button
                                onClick={() => handleRevokeGift(gift.id)}
                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                                title="Отозвать подарок"
                              >
                                <ShieldOff className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteGift(gift.id)}
                              className="p-1 text-gray-400 hover:text-red-400 hover:bg-red-900/30 rounded"
                              title="Удалить запись"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Нижняя панель с кнопками */}
        {activeTab === 'available' && (
          <div className="px-6 py-4 border-t border-gray-700 bg-gray-900/50">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-400">
                Выбрано: <span className="text-pink-400 font-medium">{selectedGiftItems.length}</span>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-300 hover:text-white"
                >
                  Отмена
                </button>
                <button
                  onClick={handleGrantGift}
                  disabled={loading || selectedGiftItems.length === 0}
                  className="px-6 py-2 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Выдача...
                    </>
                  ) : (
                    <>
                      <Gift className="w-4 h-4" />
                      Выдать подарок
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftManager;